/**
 * Per-tool deep documentation — /mcp/tools/<name>
 *
 * Single dynamic Expo Router route that renders detailed reference docs for
 * any of the 9 Inverio MCP tools. The slug is matched against the TOOL_DOCS
 * content map; unknown slugs render a 404-style card with a back link.
 *
 * Visual style mirrors /mcp (dark palette, PublicLayout, AnimatedPressable).
 * Auth-gated identically to /mcp — anonymous users are redirected to /login.
 *
 * Used as part of the Anthropic Connector application — reviewers read these
 * pages to evaluate per-tool semantics, side effects, and cost signals.
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  Platform,
  ScrollView,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import Head from 'expo-router/head';
import { useRouter, useLocalSearchParams } from 'expo-router';
import * as Clipboard from 'expo-clipboard';
import {
  Terminal,
  Copy,
  Check,
  ArrowLeft,
  Code,
  Clock,
  Database,
  AlertTriangle,
  Info,
  Zap,
  ShieldAlert,
  ChevronRight,
  Search,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeIn } from 'react-native-reanimated';
import { PublicLayout } from '@/components/PublicLayout';
import AnimatedPressable from '@/components/AnimatedPressable';
import { hapticSuccess } from '@/lib/haptics';
import { useTrackScreen } from '@/lib/analytics';
import { useIsAuthenticated } from '@/lib/supabase/auth';

// ---------------------------------------------------------------------------
// Palette — must match /mcp (dark beta theme).
// ---------------------------------------------------------------------------
const C = {
  surface: 'rgba(255,255,255,0.05)',
  surfaceElevated: 'rgba(255,255,255,0.08)',
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.15)',
  text: '#FFFFFF',
  text1: 'rgba(255,255,255,0.85)',
  text2: 'rgba(255,255,255,0.6)',
  text3: 'rgba(255,255,255,0.38)',
  brand: '#4fd1c5',
  brandSoft: 'rgba(79,209,197,0.16)',
  green: '#34D399',
  greenSoft: 'rgba(52,211,153,0.18)',
  amber: '#FF9F0A',
  amberSoft: 'rgba(255,159,10,0.14)',
  red: '#F87171',
  redSoft: 'rgba(248,113,113,0.14)',
  purple: '#A78BFA',
  purpleSoft: 'rgba(167,139,250,0.16)',
  codeBg: '#0a1628',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ToolCategory =
  | 'evaluation'
  | 'verification'
  | 'data-room'
  | 'pipeline'
  | 'feedback';

interface ToolError {
  code: string;
  description: string;
}

interface ToolDoc {
  name: string;
  category: ToolCategory;
  oneLiner: string;
  whenToUse: string;
  inputSchema: Record<string, unknown>;
  inputExample: Record<string, unknown>;
  outputExample: unknown;
  outputDescription: string;
  errors: ToolError[];
  notes?: string;
  costSignal?: string;
  sideEffects?: string;
}

const CATEGORY_META: Record<ToolCategory, { label: string; color: string; soft: string }> = {
  evaluation: { label: 'Evaluation', color: C.brand, soft: C.brandSoft },
  verification: { label: 'Verification', color: C.purple, soft: C.purpleSoft },
  'data-room': { label: 'Data Room', color: C.amber, soft: C.amberSoft },
  pipeline: { label: 'Pipeline', color: C.green, soft: C.greenSoft },
  feedback: { label: 'Feedback', color: C.brand, soft: C.brandSoft },
};

// ---------------------------------------------------------------------------
// Tool input schemas — mirrored from
// supabase/functions/_shared/api-handlers/schemas.ts (TOOL_SCHEMAS).
// Kept in sync manually; this file ships with the codebase so the docs are
// always live-deployable as a static export.
// ---------------------------------------------------------------------------

const SCHEMAS = {
  score_startup: {
    type: 'object',
    properties: {
      startupId: { type: 'string', description: 'UUID of the startup to score' },
      model: {
        type: 'string',
        description: 'LLM model to use (optional, defaults to configured model)',
      },
      confirm_no_conflict: {
        type: 'boolean',
        description:
          'Set to true to confirm this startup is NOT the closed company a previous call reported (matched_closed_company).',
      },
    },
    required: ['startupId'],
  },
  verify_claim: {
    type: 'object',
    properties: {
      startupId: { type: 'string', description: 'UUID of the startup to verify' },
      scoreEventId: {
        type: 'string',
        description: 'Specific score result to verify (optional, defaults to latest)',
      },
    },
    required: ['startupId'],
  },
  check_dataroom: {
    type: 'object',
    properties: {
      startupId: { type: 'string', description: 'UUID of the startup to check' },
    },
    required: ['startupId'],
  },
  get_score_feedback: {
    type: 'object',
    properties: {
      startupId: { type: 'string', description: 'UUID of the startup' },
      scoreEventId: {
        type: 'string',
        description: 'Specific score result (optional, defaults to latest)',
      },
    },
    required: ['startupId'],
  },
  sync_dataroom: {
    type: 'object',
    properties: {
      startupId: { type: 'string', description: 'UUID of the startup' },
      provider: {
        type: 'string',
        enum: ['google-drive', 'dropbox'],
        description: 'Data room provider',
      },
      accessToken: { type: 'string', description: 'OAuth access token for the provider' },
      folderId: { type: 'string', description: 'Root folder ID (optional)' },
    },
    required: ['startupId', 'provider', 'accessToken'],
  },
  get_dealflow: {
    type: 'object',
    properties: {
      status: {
        type: 'string',
        // The LLM sees this string as the enum. It must list every stage in
        // src/lib/dealflow/stage.ts or the model will silently never filter by
        // the missing one — and `awaiting_access` is where every deal sits
        // between asking for the data room and the founder answering.
        description: 'Filter by status: screening, interested, awaiting_access, diligence, invested, rejected, monitoring',
      },
      minScore: { type: 'number', description: 'Minimum score threshold (0-100)' },
      sector: { type: 'string', description: 'Filter by sector' },
      stage: { type: 'string', description: 'Filter by funding stage' },
      limit: { type: 'number', description: 'Max results (default 50, max 100)' },
      offset: { type: 'number', description: 'Pagination offset (default 0)' },
    },
    required: [] as string[],
  },
  get_portfolio: {
    type: 'object',
    properties: {
      limit: { type: 'number', description: 'Max results (default 50, max 100)' },
      offset: { type: 'number', description: 'Pagination offset (default 0)' },
    },
    required: [] as string[],
  },
  get_startup_detail: {
    type: 'object',
    properties: {
      startupId: { type: 'string', description: 'UUID of the startup' },
    },
    required: ['startupId'],
  },
  get_daily_briefing: {
    type: 'object',
    properties: {
      since: {
        type: 'string',
        description: 'ISO date to start from (optional, defaults to 24h ago)',
      },
    },
    required: [] as string[],
  },
} as const;

// ---------------------------------------------------------------------------
// TOOL_DOCS — full per-tool content. All 9 tools.
// ---------------------------------------------------------------------------

const TOOL_DOCS: Record<string, ToolDoc> = {
  score_startup: {
    name: 'score_startup',
    category: 'evaluation',
    oneLiner:
      'Evaluate a startup across 15 dimensions using the Inverio methodology.',
    whenToUse:
      "Call when you need a fresh score or when material new evidence has been added to the startup's data room. Side effects — creates a new score_results row that other tools and resources will subsequently reference. Don't call repeatedly without new evidence; the result will be substantially the same and the cost is non-trivial (5-30s LLM call).",
    inputSchema: SCHEMAS.score_startup,
    inputExample: {
      startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
      model: 'mistral-large-latest',
    },
    outputExample: {
      scoreEventId: '8f3a0e2d-1c4b-4f5e-9a6d-7b8c9d0e1f2a',
      overallScore: 78,
      readinessLevel: 'strong',
      dimensions: [
        {
          dimension: 'team',
          rawScore: 85,
          evidenceLevel: 'strong',
          riskLevel: 'low',
          feedback: 'Founding team has prior exits and deep domain expertise.',
          strength: 'Two prior exits, both in adjacent verticals.',
          weakness: 'Lacks senior commercial leadership for scale-up phase.',
          subScores: [
            { name: 'founder_experience', score: 90 },
            { name: 'team_completeness', score: 75 },
          ],
        },
        {
          dimension: 'market',
          rawScore: 72,
          evidenceLevel: 'medium',
          riskLevel: 'medium',
          feedback: 'Large TAM, some concerns about regulatory headwinds.',
          strength: 'EU TAM estimated at €4.2B with 18% CAGR.',
          weakness: 'New EU regulation pending could affect pricing power.',
          subScores: [],
        },
        // ... 13 more dimensions follow this exact shape
      ],
      risks: [
        { severity: 'high', description: 'Concentration risk — 60% revenue from one customer.' },
        { severity: 'medium', description: 'Cap table imbalance flagged for DD review.' },
      ],
      recommendations: [
        'Add a senior commercial hire before next round.',
        'Diversify customer base; target <30% from any single account.',
        'Engage regulatory counsel on the pending EU directive.',
      ],
    },
    outputDescription:
      'Returns the full scoring result. The 15 dimensions follow the proprietary Inverio methodology. Overall score is 0-100; readinessLevel is critical / weak / moderate / strong / excellent. SubScores per dimension may be empty arrays in some runs (LLM reliability variation — see Phase 1.5 follow-up). Evidence quotes and their sources are the founder\'s proof material: they are returned only while you hold data-room access to the startup. Without it each sub-score carries evidenceSummary {citations} and claimsSummary {verified, contradicted, unverifiable, unchecked} instead, and scoreResult.rawInputsVisible is false.',
    errors: [
      { code: '401', description: 'Invalid or missing API key.' },
      { code: '404', description: 'Startup not found, or not accessible to this API key.' },
      {
        code: '409',
        description:
          'matched_closed_company — the startup matches a company that has closed on Inverio. Nothing was scored or charged; the error carries the closed company\'s name and status. Repeat the call with confirm_no_conflict: true if it is a different company.',
      },
      { code: '429', description: 'Rate limited — back off and retry.' },
      { code: '500', description: 'LLM provider error — retry after a short delay.' },
    ],
    costSignal: 'slow (5-30s) — uses LLM',
    sideEffects: 'Creates a new score_results row.',
    notes:
      'The Inverio methodology weights dimensions by investor archetype (default = generalist seed). Repeated calls without new evidence are wasteful and produce near-identical output.',
  },
  verify_claim: {
    name: 'verify_claim',
    category: 'verification',
    oneLiner:
      'Verify startup claims against external sources — registries, web, public data.',
    whenToUse:
      'Run after a score has been produced when the user wants evidence-grade confidence. Verifies claims against external sources (registries, web search, public data). Generates a verification_results row per claim — designed to be incremental, can be called multiple times to refresh.',
    inputSchema: SCHEMAS.verify_claim,
    inputExample: {
      startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
      scoreEventId: '8f3a0e2d-1c4b-4f5e-9a6d-7b8c9d0e1f2a',
    },
    outputExample: {
      summary: {
        total: 23,
        confirmed: 17,
        contradicted: 1,
        unverifiable: 5,
        ddFlagged: 2,
      },
      claims: [
        {
          type: 'company_registration',
          assertion: 'Registered in Zurich since 2022',
          status: 'confirmed',
          sourceName: 'Zefix',
          sourceUrl: 'https://www.zefix.ch/en/search/entity/list?name=Acme%20AG',
          verifiedAt: '2026-05-01T08:32:11Z',
        },
        {
          type: 'team_background',
          assertion: 'CEO previously founded Beta Corp (acquired 2019)',
          status: 'confirmed',
          sourceName: 'LinkedIn / Crunchbase',
          sourceUrl: 'https://www.crunchbase.com/organization/beta-corp',
          verifiedAt: '2026-05-01T08:32:42Z',
        },
        {
          type: 'revenue_milestone',
          assertion: '€2M ARR achieved Q1 2026',
          status: 'unverifiable',
          sourceName: null,
          sourceUrl: null,
          verifiedAt: '2026-05-01T08:33:01Z',
        },
      ],
    },
    outputDescription:
      'Aggregated verification report. ddFlagged is the count of claims requiring DD-level human review. Claim text (entity, assertion, source quote) and verification sources (name, summary, URL) are returned only while you hold data-room access to the startup; without it you receive statuses and counts, and claimTextWithheld is true.',
    errors: [
      { code: '401', description: 'Invalid or missing API key.' },
      { code: '404', description: 'No score yet for this startup — call score_startup first.' },
      { code: '429', description: 'Rate limited — external sources have their own quotas.' },
      { code: '500', description: 'External provider failure — retry after a short delay.' },
    ],
    costSignal: 'slow (10-60s) — fetches external sources',
    sideEffects: 'Creates verification_results rows.',
    notes:
      'Each claim gets one verification_results row. Re-running this tool refreshes any expired entries (>30 days old) and adds new claims that appeared in subsequent scores.',
  },
  check_dataroom: {
    name: 'check_dataroom',
    category: 'data-room',
    oneLiner:
      'Document completeness and deal readiness for a startup data room.',
    whenToUse:
      'Read-only readiness snapshot. Returns required vs present vs missing documents and a completeness percentage. Idempotent and fast — call freely.',
    inputSchema: SCHEMAS.check_dataroom,
    inputExample: {
      startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
    },
    outputExample: {
      totalRequired: 12,
      present: 9,
      missing: 3,
      completenessPct: 75,
      missingCritical: ['cap_table', 'financial_model'],
      categories: [
        { category: 'financials', required: 4, present: 3 },
        { category: 'legal', required: 3, present: 3 },
        { category: 'team', required: 2, present: 2 },
        { category: 'product', required: 3, present: 1 },
      ],
    },
    outputDescription:
      "Document readiness for the startup. completenessPct is rounded; missingCritical lists doc types tagged as critical that aren't yet uploaded.",
    errors: [
      { code: '401', description: 'Invalid or missing API key.' },
      { code: '404', description: 'Startup not found, or not accessible to this API key.' },
    ],
    costSignal: 'fast (<500ms)',
    notes:
      'Pairs naturally with sync_dataroom — call check_dataroom first to see what is missing, then sync_dataroom to pull new uploads from the connected drive.',
  },
  get_score_feedback: {
    name: 'get_score_feedback',
    category: 'feedback',
    oneLiner: 'Scoring feedback with improvement guidance per dimension.',
    whenToUse:
      "Read-only — pull the most recent score's improvement guidance. Includes per-dimension recommendations and the venture label. Designed for founder-facing agents.",
    inputSchema: SCHEMAS.get_score_feedback,
    inputExample: {
      startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
      scoreEventId: '8f3a0e2d-1c4b-4f5e-9a6d-7b8c9d0e1f2a',
    },
    outputExample: {
      overallScore: 78,
      ventureLabel: 'strong',
      topRecommendations: [
        'Add a senior commercial hire before the next round.',
        'Diversify customer concentration below 30% per account.',
        'Publish a security & compliance posture for enterprise sales.',
      ],
      dimensionFeedback: [
        {
          dimension: 'team',
          score: 85,
          feedback: 'Strong founder-market fit with proven exits.',
          actionItems: [
            'Hire a VP Sales with B2B SaaS scale-up experience.',
            'Document succession planning for key founders.',
          ],
        },
        {
          dimension: 'market',
          score: 72,
          feedback: 'Large TAM but regulatory headwinds need monitoring.',
          actionItems: [
            'Engage regulatory counsel on the pending EU directive.',
            'Prepare a contingency pricing model.',
          ],
        },
      ],
    },
    outputDescription:
      'Action-oriented score feedback. ventureLabel matches the methodology 5-band scale.',
    errors: [
      { code: '401', description: 'Invalid or missing API key.' },
      { code: '404', description: 'No score yet for this startup — call score_startup first.' },
    ],
    costSignal: 'fast',
    notes:
      'If scoreEventId is omitted, the latest score is used. Pass it explicitly when comparing feedback across two runs.',
  },
  sync_dataroom: {
    name: 'sync_dataroom',
    category: 'data-room',
    oneLiner: 'Trigger a delta-aware resync of a connected data room.',
    whenToUse:
      'Trigger a delta-aware resync of a connected drive (Google Drive or Dropbox). Use when the user mentions adding new docs to their drive. Side effects — updates document_classifications and may trigger AI rescoring downstream.',
    inputSchema: SCHEMAS.sync_dataroom,
    inputExample: {
      startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
      provider: 'google-drive',
      accessToken: 'ya29.a0AfH6SMB...',
      folderId: '1A2B3C4D5E6F7G8H9I0J',
    },
    outputExample: {
      added: 3,
      changed: 1,
      deleted: 0,
      totalAfter: 14,
      classified: 4,
    },
    outputDescription:
      'Sync delta summary. classified is the count newly-classified into checklist categories.',
    errors: [
      { code: '401', description: 'Invalid or missing API key.' },
      { code: '404', description: 'Startup not found, or not accessible to this API key.' },
      { code: '429', description: 'Rate limited — provider quotas apply per drive.' },
      {
        code: '500',
        description: 'Provider auth failure — refresh the OAuth access token and retry.',
      },
    ],
    costSignal: 'slow (5-60s)',
    sideEffects:
      'Updates document_classifications, dataroom_sync_manifest, may trigger downstream rescoring.',
    notes:
      'Delta-aware: only changed/added/deleted files since the last manifest are processed. The accessToken is not stored — it is used in-flight only.',
  },
  get_dealflow: {
    name: 'get_dealflow',
    category: 'pipeline',
    oneLiner: 'Investor dealflow pipeline with status, score, sector, stage filters.',
    whenToUse:
      "Read-only — fetch the investor's deal pipeline with optional filters. Default returns first 50 ordered by recency.",
    inputSchema: SCHEMAS.get_dealflow,
    inputExample: {
      status: 'screening',
      minScore: 70,
      sector: 'fintech',
      stage: 'seed',
      limit: 20,
      offset: 0,
    },
    outputExample: {
      items: [
        {
          startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
          name: 'Acme AG',
          sector: 'fintech',
          stage: 'seed',
          score: 78,
          readinessLevel: 'strong',
          status: 'screening',
          submittedAt: '2026-04-12T10:00:00Z',
          lastScoredAt: '2026-04-30T08:30:00Z',
        },
        {
          startupId: '22850932-a349-5f0c-ad51-17e5941b735g',
          name: 'Beta Labs',
          sector: 'fintech',
          stage: 'seed',
          score: 81,
          readinessLevel: 'strong',
          status: 'screening',
          submittedAt: '2026-04-18T14:22:00Z',
          lastScoredAt: '2026-04-29T09:15:00Z',
        },
      ],
      total: 47,
    },
    outputDescription:
      'Paginated dealflow. total is the count after status filter (before minScore/sector/stage post-filters).',
    errors: [{ code: '401', description: 'Invalid or missing API key.' }],
    costSignal: 'fast',
    notes:
      'Sorted by lastScoredAt desc by default. Use offset/limit for pagination; total is independent of the paging window.',
  },
  get_portfolio: {
    name: 'get_portfolio',
    category: 'pipeline',
    oneLiner: 'Portfolio companies marked as invested with latest scores.',
    whenToUse:
      "Read-only — fetch the investor's portfolio (deals with status='invested'). Designed for portfolio-tracking agents.",
    inputSchema: SCHEMAS.get_portfolio,
    inputExample: { limit: 50, offset: 0 },
    outputExample: {
      items: [
        {
          startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
          name: 'Acme AG',
          sector: 'fintech',
          stage: 'series-a',
          latestScore: 82,
          investedAt: '2025-11-04T12:00:00Z',
          amount: null,
        },
        {
          startupId: '33960043-b450-6f1d-be62-28f6052c846h',
          name: 'Gamma Industries',
          sector: 'climate',
          stage: 'seed',
          latestScore: 76,
          investedAt: '2026-02-19T09:30:00Z',
          amount: null,
        },
      ],
      total: 12,
    },
    outputDescription:
      'Paginated portfolio. amount is currently always null — investment amounts will become first-class in Phase 3 of the data-contract roadmap.',
    errors: [{ code: '401', description: 'Invalid or missing API key.' }],
    costSignal: 'fast',
    notes:
      "Resource alternative — read inverio://user/me/portfolio at conversation start instead of calling this tool every turn.",
  },
  get_startup_detail: {
    name: 'get_startup_detail',
    category: 'pipeline',
    oneLiner: 'Full startup profile with score, verification, and checklist summary.',
    whenToUse:
      "Read-only deep dive — full startup profile + latest score + verification summary + checklist summary. Best for 'tell me everything about startup X' prompts.",
    inputSchema: SCHEMAS.get_startup_detail,
    inputExample: {
      startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
    },
    outputExample: {
      profile: {
        startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
        name: 'Acme AG',
        sector: 'fintech',
        stage: 'seed',
        problem: 'SMEs in DACH lack accessible cross-border payment rails.',
        solution: 'Embedded multi-currency wallet + FX hedging API.',
        country: 'CH',
        foundedAt: '2022-03-15',
      },
      latestScore: {
        scoreEventId: '8f3a0e2d-1c4b-4f5e-9a6d-7b8c9d0e1f2a',
        overallScore: 78,
        readinessLevel: 'strong',
        scoredAt: '2026-04-30T08:30:00Z',
      },
      verificationSummary: {
        total: 23,
        confirmed: 17,
        contradicted: 1,
        unverifiable: 5,
        ddFlagged: 2,
      },
      dataroom: {
        completenessPct: 75,
        present: 9,
        missing: 3,
        missingCritical: ['cap_table', 'financial_model'],
      },
      lastUpdated: '2026-04-30T08:30:00Z',
    },
    outputDescription:
      'Composite of profile + score + verification + dataroom. Use this instead of multiple resource reads when an agent has a single broad intent.',
    errors: [
      { code: '401', description: 'Invalid or missing API key.' },
      { code: '404', description: 'Startup not found, or not accessible to this API key.' },
    ],
    costSignal: 'medium (single round-trip with joins)',
    notes:
      'If you only need one section (e.g., verification report), prefer the matching resource — it is cheaper and cacheable.',
  },
  get_daily_briefing: {
    name: 'get_daily_briefing',
    category: 'pipeline',
    oneLiner:
      'Aggregated briefing — new submissions, score changes, portfolio alerts.',
    whenToUse:
      'Read-only morning-briefing aggregate — new submissions, score changes, portfolio alerts since a given date. Designed for daily summary loops.',
    inputSchema: SCHEMAS.get_daily_briefing,
    inputExample: { since: '2026-04-30T00:00:00Z' },
    outputExample: {
      period: {
        from: '2026-04-30T00:00:00Z',
        to: '2026-05-01T00:00:00Z',
      },
      newSubmissions: 3,
      scoreChanges: [
        {
          startupId: '11740821-9238-4f0b-9d40-06d4830a624f',
          name: 'Acme AG',
          delta: 12,
          newScore: 78,
        },
        {
          startupId: '44070154-c561-7f2e-cf73-39g7163d957i',
          name: 'Delta Robotics',
          delta: -4,
          newScore: 64,
        },
      ],
      portfolioAlerts: [
        {
          startupId: '33960043-b450-6f1d-be62-28f6052c846h',
          name: 'Gamma Industries',
          alert: 'Verification flagged 2 contradictions in revenue claims.',
          severity: 'medium',
        },
      ],
    },
    outputDescription: 'Time-bounded activity summary.',
    errors: [{ code: '401', description: 'Invalid or missing API key.' }],
    costSignal: 'fast',
    notes:
      'Defaults to the last 24 hours when since is omitted. Designed to be called once per day in a morning briefing loop — caching is out of scope.',
  },
};

const TOOL_NAMES = Object.keys(TOOL_DOCS);

// ---------------------------------------------------------------------------
// Sub-components — copy/snippet patterns mirror /mcp.tsx
// ---------------------------------------------------------------------------

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    await Clipboard.setStringAsync(value);
    hapticSuccess();
    setCopied(true);
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => setCopied(false), 2200);
  }, [value]);

  return (
    <AnimatedPressable
      onPress={handleCopy}
      className="flex-row items-center justify-center gap-1.5 px-3 py-2 rounded-md"
      style={{
        backgroundColor: copied ? C.greenSoft : C.surfaceElevated,
        minWidth: label ? 90 : 36,
        borderWidth: 1,
        borderColor: copied ? C.green : C.border,
      }}
      accessibilityRole="button"
      accessibilityLabel={copied ? 'Copied' : `Copy ${label ?? 'value'}`}
    >
      {copied ? <Check size={14} color={C.green} /> : <Copy size={14} color={C.text2} />}
      {label ? (
        <Text
          style={{ color: copied ? C.green : C.text1, fontFamily: 'monospace' }}
          className="text-xs font-medium"
          numberOfLines={1}
        >
          {copied ? 'Copied' : label}
        </Text>
      ) : null}
    </AnimatedPressable>
  );
}

function JsonBlock({
  title,
  data,
  icon,
}: {
  title: string;
  data: unknown;
  icon?: React.ReactNode;
}) {
  const json = useMemo(() => JSON.stringify(data, null, 2), [data]);
  return (
    <View
      className="rounded-xl mt-3 overflow-hidden"
      style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
    >
      <View
        className="flex-row items-center justify-between px-4 py-2.5"
        style={{ borderBottomWidth: 1, borderBottomColor: C.border }}
      >
        <View className="flex-row items-center gap-2">
          {icon ?? <Terminal size={13} color={C.text2} />}
          <Text style={{ color: C.text }} className="text-sm font-semibold" numberOfLines={1}>
            {title}
          </Text>
        </View>
        <CopyButton value={json} label="Copy" />
      </View>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ padding: 14 }}
        style={{ backgroundColor: C.codeBg }}
      >
        <Text
          style={{ color: '#E6FCFC', fontFamily: 'monospace' }}
          className="text-xs leading-5"
          selectable
        >
          {json}
        </Text>
      </ScrollView>
    </View>
  );
}

/**
 * Render the JSON-Schema "properties" + "required" as a friendly description list.
 * Falls back gracefully if the schema doesn't have the expected shape.
 */
function SchemaPropertiesList({ schema }: { schema: Record<string, unknown> }) {
  const properties = (schema.properties ?? {}) as Record<
    string,
    { type?: string; description?: string; enum?: string[] }
  >;
  const required = (schema.required ?? []) as string[];
  const entries = Object.entries(properties);
  if (entries.length === 0) {
    return (
      <Text style={{ color: C.text3 }} className="text-xs italic mt-2">
        No input parameters.
      </Text>
    );
  }
  return (
    <View className="mt-3 gap-2">
      {entries.map(([name, prop]) => {
        const isRequired = required.includes(name);
        return (
          <View
            key={name}
            className="px-3 py-2.5 rounded-lg"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            <View className="flex-row items-center gap-2 flex-wrap mb-1">
              <Text
                style={{ color: C.brand, fontFamily: 'monospace' }}
                className="text-xs font-semibold"
                numberOfLines={1}
              >
                {name}
              </Text>
              <Text
                style={{ color: C.text3, fontFamily: 'monospace' }}
                className="text-xs"
                numberOfLines={1}
              >
                {prop.type ?? 'unknown'}
              </Text>
              <View
                className="px-1.5 py-0.5 rounded"
                style={{ backgroundColor: isRequired ? C.amberSoft : C.surfaceElevated }}
              >
                <Text
                  style={{
                    color: isRequired ? C.amber : C.text3,
                    fontSize: 10,
                  }}
                  className="font-bold uppercase"
                  numberOfLines={1}
                >
                  {isRequired ? 'required' : 'optional'}
                </Text>
              </View>
              {prop.enum ? (
                <Text
                  style={{ color: C.text3, fontFamily: 'monospace' }}
                  className="text-xs"
                  numberOfLines={1}
                >
                  enum: {prop.enum.join(' | ')}
                </Text>
              ) : null}
            </View>
            {prop.description ? (
              <Text style={{ color: C.text2 }} className="text-xs leading-5">
                {prop.description}
              </Text>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

function MetaBadgeRow({
  costSignal,
  sideEffects,
}: {
  costSignal?: string;
  sideEffects?: string;
}) {
  if (!costSignal && !sideEffects) return null;
  return (
    <View className="flex-row flex-wrap gap-2 mb-5">
      {costSignal ? (
        <View
          className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-full"
          style={{ backgroundColor: C.surfaceElevated, borderWidth: 1, borderColor: C.border }}
        >
          <Clock size={12} color={C.amber} />
          <Text
            style={{ color: C.text1 }}
            className="text-xs font-medium"
            numberOfLines={1}
          >
            {costSignal}
          </Text>
        </View>
      ) : null}
      {sideEffects ? (
        <View
          className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-full"
          style={{ backgroundColor: C.amberSoft, borderWidth: 1, borderColor: C.border }}
        >
          <Database size={12} color={C.amber} />
          <Text
            style={{ color: C.text1, flexShrink: 1 }}
            className="text-xs font-medium"
            numberOfLines={2}
          >
            {sideEffects}
          </Text>
        </View>
      ) : (
        <View
          className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-full"
          style={{ backgroundColor: C.greenSoft, borderWidth: 1, borderColor: C.border }}
        >
          <Database size={12} color={C.green} />
          <Text
            style={{ color: C.green }}
            className="text-xs font-medium"
            numberOfLines={1}
          >
            Read-only
          </Text>
        </View>
      )}
    </View>
  );
}

function SectionHeader({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <View className="flex-row items-center gap-2 mb-2 mt-7">
      {icon}
      <Text style={{ color: C.text }} className="text-base font-bold" numberOfLines={1}>
        {title}
      </Text>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Not-found card
// ---------------------------------------------------------------------------

function ToolNotFound({ slug }: { slug: string }) {
  const router = useRouter();
  return (
    <View
      className="rounded-xl p-6 items-center"
      style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
    >
      <View
        className="w-12 h-12 rounded-full items-center justify-center mb-3"
        style={{ backgroundColor: C.redSoft }}
      >
        <Search size={20} color={C.red} />
      </View>
      <Text
        style={{ color: C.text }}
        className="text-base font-bold mb-1 text-center"
        numberOfLines={1}
      >
        Tool not found
      </Text>
      <Text style={{ color: C.text2 }} className="text-sm leading-5 text-center mb-4">
        No MCP tool matches{' '}
        <Text style={{ fontFamily: 'monospace', color: C.text1 }}>{slug || '(empty)'}</Text>.
        The Inverio MCP server exposes {TOOL_NAMES.length} tools — see the list below.
      </Text>
      <View className="w-full gap-1.5 mb-4">
        {TOOL_NAMES.map((n) => (
          <AnimatedPressable
            key={n}
            onPress={() => router.replace(`/mcp/tools/${n}`)}
            className="flex-row items-center justify-between px-3 py-2 rounded-md"
            style={{ backgroundColor: C.surfaceElevated, borderWidth: 1, borderColor: C.border }}
            accessibilityRole="link"
            accessibilityLabel={`Open ${n} documentation`}
          >
            <Text
              style={{ color: C.brand, fontFamily: 'monospace' }}
              className="text-xs font-semibold"
              numberOfLines={1}
            >
              {n}
            </Text>
            <ChevronRight size={14} color={C.text3} />
          </AnimatedPressable>
        ))}
      </View>
      <AnimatedPressable
        onPress={() => router.replace('/mcp')}
        className="flex-row items-center self-center gap-1.5 px-3 py-2 rounded-md"
        style={{ backgroundColor: C.brand }}
        accessibilityRole="button"
        accessibilityLabel="Back to MCP overview"
      >
        <ArrowLeft size={14} color="#0a1628" />
        <Text style={{ color: '#0a1628' }} className="text-sm font-bold" numberOfLines={1}>
          Back to MCP overview
        </Text>
      </AnimatedPressable>
    </View>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function McpToolDetailPage() {
  useTrackScreen('McpToolDetail');
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const { isAuthenticated, isLoading: authLoading } = useIsAuthenticated();
  const { name } = useLocalSearchParams<{ name: string }>();
  const slug = typeof name === 'string' ? name : '';

  const doc = TOOL_DOCS[slug];

  // Auth gate — same behavior as /mcp.tsx
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      router.replace('/login');
    }
  }, [authLoading, isAuthenticated, router]);

  // Inject OG meta tags (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!doc) return;
    const og: [string, string][] = [
      ['og:type', 'article'],
      ['og:url', `https://mcp.inverio.ai/tools/${doc.name}`],
      ['og:title', `Inverio MCP — ${doc.name}`],
      ['og:description', doc.oneLiner],
      ['og:image', 'https://inverio.ai/og-image.png'],
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', `Inverio MCP — ${doc.name}`],
      ['twitter:description', doc.oneLiner],
      ['twitter:image', 'https://inverio.ai/og-image.png'],
    ];
    const created: HTMLMetaElement[] = [];
    og.forEach(([prop, content]) => {
      const m = document.createElement('meta');
      m.setAttribute(prop.startsWith('og:') ? 'property' : 'name', prop);
      m.setAttribute('content', content);
      m.id = `mcp-tool-og-${prop}`;
      document.head.appendChild(m);
      created.push(m);
    });
    return () => created.forEach((m) => m.remove());
  }, [doc]);

  // While auth is resolving or redirecting, show a minimal splash to avoid
  // flashing the page contents to anonymous users.
  if (authLoading || !isAuthenticated) {
    return (
      <View
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: '#0a1628' }}
      >
        <ActivityIndicator color={C.brand} />
      </View>
    );
  }

  // Unknown slug → 404 card
  if (!doc) {
    return (
      <PublicLayout title="Tool not found" titleIcon={<Code size={22} color={C.text} />} maxWidth={920}>
        <Head>
          <title>Inverio MCP — Tool not found</title>
        </Head>
        <View style={{ paddingTop: isDesktop ? 24 : 8, paddingBottom: 64 }}>
          <Animated.View entering={FadeInDown.duration(400)}>
            <AnimatedPressable
              onPress={() => router.replace('/mcp')}
              className="flex-row items-center self-start gap-1.5 px-2 py-1 mb-4 rounded-md"
              accessibilityRole="link"
              accessibilityLabel="Back to MCP overview"
            >
              <ArrowLeft size={14} color={C.text2} />
              <Text style={{ color: C.text2 }} className="text-xs font-medium" numberOfLines={1}>
                MCP / Tools / not-found
              </Text>
            </AnimatedPressable>
          </Animated.View>
          <ToolNotFound slug={slug} />
        </View>
      </PublicLayout>
    );
  }

  const cat = CATEGORY_META[doc.category];

  return (
    <PublicLayout
      title={doc.name}
      titleIcon={<Code size={22} color={C.text} />}
      maxWidth={920}
    >
      <Head>
        <title>{`Inverio MCP — ${doc.name}`}</title>
        <meta name="description" content={doc.oneLiner} />
      </Head>

      <View style={{ paddingTop: isDesktop ? 24 : 8, paddingBottom: 64 }}>
        {/* Breadcrumb */}
        <Animated.View entering={FadeInDown.duration(400)}>
          <AnimatedPressable
            onPress={() => router.push('/mcp')}
            className="flex-row items-center self-start gap-1.5 px-2 py-1 mb-4 rounded-md"
            accessibilityRole="link"
            accessibilityLabel="Back to MCP overview"
          >
            <ArrowLeft size={14} color={C.text2} />
            <Text style={{ color: C.text2 }} className="text-xs font-medium" numberOfLines={1}>
              MCP / Tools / {doc.name}
            </Text>
          </AnimatedPressable>
        </Animated.View>

        {/* Hero */}
        <Animated.View entering={FadeInDown.delay(80).duration(400)}>
          <View className="flex-row items-center gap-2 mb-4 flex-wrap">
            <View
              className="px-2.5 py-1 rounded-full"
              style={{ backgroundColor: cat.soft }}
            >
              <Text
                style={{ color: cat.color }}
                className="text-xs font-bold uppercase"
                numberOfLines={1}
              >
                {cat.label}
              </Text>
            </View>
            <Text style={{ color: C.text3 }} className="text-xs" numberOfLines={1}>
              MCP tool
            </Text>
          </View>

          <Text
            className="font-bold mb-3"
            style={{
              fontSize: isDesktop ? 40 : 28,
              lineHeight: isDesktop ? 48 : 34,
              color: C.text,
              fontFamily: 'monospace',
            }}
            selectable
          >
            {doc.name}
          </Text>
          <Text
            style={{
              color: C.text2,
              fontSize: isDesktop ? 17 : 15,
              lineHeight: isDesktop ? 26 : 22,
            }}
            className="mb-5"
          >
            {doc.oneLiner}
          </Text>

          {/* Cost / side effects badges */}
          <MetaBadgeRow costSignal={doc.costSignal} sideEffects={doc.sideEffects} />
        </Animated.View>

        {/* When to use */}
        <Animated.View entering={FadeInDown.delay(140).duration(400)}>
          <SectionHeader
            icon={<Info size={16} color={C.text} />}
            title="When to use"
          />
          <View
            className="rounded-xl p-4"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            <Text style={{ color: C.text1 }} className="text-sm leading-6">
              {doc.whenToUse}
            </Text>
          </View>
        </Animated.View>

        {/* Input */}
        <Animated.View entering={FadeInDown.delay(200).duration(400)}>
          <SectionHeader
            icon={<Zap size={16} color={C.text} />}
            title="Input"
          />
          <Text style={{ color: C.text2 }} className="text-sm leading-5">
            Parameters accepted by{' '}
            <Text style={{ fontFamily: 'monospace', color: C.brand }}>{doc.name}</Text>.
          </Text>
          <SchemaPropertiesList schema={doc.inputSchema} />
          <JsonBlock
            title="Example request"
            data={doc.inputExample}
            icon={<Terminal size={13} color={C.text2} />}
          />
        </Animated.View>

        {/* Output */}
        <Animated.View entering={FadeInDown.delay(260).duration(400)}>
          <SectionHeader
            icon={<Database size={16} color={C.text} />}
            title="Output"
          />
          <Text style={{ color: C.text2 }} className="text-sm leading-5">
            {doc.outputDescription}
          </Text>
          <JsonBlock
            title="Example response"
            data={doc.outputExample}
            icon={<Database size={13} color={C.text2} />}
          />
        </Animated.View>

        {/* Errors */}
        <Animated.View entering={FadeInDown.delay(320).duration(400)}>
          <SectionHeader
            icon={<AlertTriangle size={16} color={C.text} />}
            title="Errors"
          />
          <View
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            {doc.errors.map((err, idx) => (
              <View
                key={err.code}
                className="flex-row items-start gap-3 px-4 py-3"
                style={{
                  borderBottomWidth: idx === doc.errors.length - 1 ? 0 : 1,
                  borderBottomColor: C.border,
                }}
              >
                <View
                  className="px-2 py-0.5 rounded"
                  style={{ backgroundColor: C.redSoft, minWidth: 44, alignItems: 'center' }}
                >
                  <Text
                    style={{ color: C.red, fontFamily: 'monospace' }}
                    className="text-xs font-bold"
                    numberOfLines={1}
                  >
                    {err.code}
                  </Text>
                </View>
                <Text
                  style={{ color: C.text1, flex: 1, minWidth: 0 }}
                  className="text-sm leading-5"
                >
                  {err.description}
                </Text>
              </View>
            ))}
          </View>
        </Animated.View>

        {/* Notes (optional) */}
        {doc.notes ? (
          <Animated.View entering={FadeInDown.delay(380).duration(400)}>
            <SectionHeader
              icon={<ShieldAlert size={16} color={C.text} />}
              title="Notes"
            />
            <View
              className="rounded-xl p-4 flex-row items-start gap-3"
              style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
            >
              <View
                className="w-8 h-8 rounded-lg items-center justify-center"
                style={{ backgroundColor: C.amberSoft }}
              >
                <Info size={14} color={C.amber} />
              </View>
              <Text style={{ color: C.text2, flex: 1 }} className="text-sm leading-6">
                {doc.notes}
              </Text>
            </View>
          </Animated.View>
        ) : null}

        {/* Sibling tools — quick nav */}
        <Animated.View entering={FadeIn.delay(440).duration(500)}>
          <SectionHeader
            icon={<ChevronRight size={16} color={C.text} />}
            title="Other tools"
          />
          <View
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: C.surface, borderWidth: 1, borderColor: C.border }}
          >
            {TOOL_NAMES.filter((n) => n !== doc.name).map((n, idx, arr) => {
              const sibling = TOOL_DOCS[n];
              const sCat = CATEGORY_META[sibling.category];
              return (
                <AnimatedPressable
                  key={n}
                  onPress={() => router.push(`/mcp/tools/${n}`)}
                  className="flex-row items-center gap-3 px-4 py-3"
                  style={{
                    borderBottomWidth: idx === arr.length - 1 ? 0 : 1,
                    borderBottomColor: C.border,
                  }}
                  accessibilityRole="link"
                  accessibilityLabel={`Open ${n} documentation`}
                >
                  <View
                    className="px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: sCat.soft, minWidth: 80, alignItems: 'center' }}
                  >
                    <Text
                      style={{ color: sCat.color }}
                      className="text-[10px] font-bold uppercase"
                      numberOfLines={1}
                    >
                      {sCat.label}
                    </Text>
                  </View>
                  <Text
                    style={{ color: C.brand, fontFamily: 'monospace' }}
                    className="text-sm font-semibold"
                    numberOfLines={1}
                  >
                    {n}
                  </Text>
                  <Text
                    style={{ color: C.text2, flex: 1, minWidth: 0 }}
                    className="text-xs leading-5"
                    numberOfLines={1}
                  >
                    {sibling.oneLiner}
                  </Text>
                  <ChevronRight size={14} color={C.text3} />
                </AnimatedPressable>
              );
            })}
          </View>
        </Animated.View>
      </View>
    </PublicLayout>
  );
}
